import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import type { ReactNode } from 'react';
import type { AuthConfig } from '../api/types.ts';

/**
 * Where the user was when their session ran out, so sign-in can put them back.
 * Session-scoped: it is a breadcrumb for one redirect, not a preference.
 */
const RETURN_TO = 'procura.returnTo';
const DEV_TOKEN = 'procura.devToken';

export interface Session {
  config: AuthConfig;
  isSignedIn: boolean;
  /** Why the last session ended, if one did; null after a sign-in. */
  signInReason: SignInReason | null;
  /** Resolves a usable access token, refreshing silently if it can. */
  getToken: () => Promise<string>;
  signIn: (devEmail?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

/**
 * The session ran out, told once, to the shell.
 *
 * `apiFetch` throws `NeedsSignIn` on a 401 with the stated intention that "the
 * shell redirects once, rather than each screen inventing its own handling".
 * Nothing ever caught it. So it arrived at whichever screen made the request as
 * an ordinary query error, and that screen showed both its own error *and*
 * `Staleness` reporting "Cannot reach the server" — two messages for one cause,
 * and neither of them the true one. An expired token is not a connectivity
 * failure and must not read as one.
 *
 * A module-level signal rather than context, because `api/client.ts` is not a
 * component and should not have to reach into React to say this.
 */
/**
 * Why the session ended, because the sign-in screen has to say which.
 *
 * `expired`: there was no token, or it could not be refreshed — ordinary.
 * `rejected`: the server refused a token the browser had. Signing in again
 * gets another token from the same place, and if the server refused the last
 * one it will likely refuse that too. Shown without a reason, that is a loop
 * that looks like the sign-in button not working, which is exactly how the
 * Azure audience fault presented for two sessions.
 */
export type SignInReason = 'expired' | 'rejected';

type SignInListener = (reason: SignInReason) => void;
const signInListeners = new Set<SignInListener>();

/** Called by apiFetch when the server rejects the token, and by getToken. */
export function announceSignInNeeded(reason: SignInReason = 'expired'): void {
  for (const listener of signInListeners) listener(reason);
}

function onSignInNeeded(listener: SignInListener): () => void {
  signInListeners.add(listener);
  return () => signInListeners.delete(listener);
}

export function rememberWhereWeWere(): void {
  sessionStorage.setItem(RETURN_TO, location.pathname + location.search);
}

export function whereWeWere(): string | null {
  const to = sessionStorage.getItem(RETURN_TO);
  if (to) sessionStorage.removeItem(RETURN_TO);
  return to;
}

/**
 * Sign-in, told by the server which kind it is.
 *
 * No session length is hardcoded anywhere in here, deliberately. Entra's
 * refresh token lifetime is tenant policy — 24 hours by default today, seven
 * days once Conditional Access can enforce it — and this code behaves
 * identically either way: refresh silently while the refresh token lasts, and
 * when it stops working, redirect and come back to the same page.
 */
export function SessionProvider(
  { config, children }: { config: AuthConfig; children: ReactNode },
) {
  const [isSignedIn, setSignedIn] = useState(
    () => config.mode === 'local' && !!localStorage.getItem(DEV_TOKEN),
  );
  // MSAL is loaded only in entra mode, so local development never downloads it.
  const msal = useRef<import('@azure/msal-browser').PublicClientApplication | null>(null);
  const [ready, setReady] = useState(config.mode === 'local');
  const [signInReason, setSignInReason] = useState<SignInReason | null>(null);

  useEffect(() => {
    if (config.mode !== 'entra') return;
    let cancelled = false;

    void (async () => {
      const { PublicClientApplication } = await import('@azure/msal-browser');
      const app = new PublicClientApplication({
        auth: {
          clientId: config.clientId,
          authority: config.authority,
          redirectUri: location.origin,
          navigateToLoginRequestUrl: false,
        },
        cache: {
          // Persisted, so a case manager is not signing in between visits and
          // a desktop user is not signing in all morning. The tablets are
          // single-assignment, PIN-locked and remotely wipeable; the device
          // lock is the control here, and a lost tablet is a wipe.
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false,
        },
      });
      await app.initialize();
      const result = await app.handleRedirectPromise();
      if (cancelled) return;

      if (result?.account) app.setActiveAccount(result.account);
      else {
        const [first] = app.getAllAccounts();
        if (first) app.setActiveAccount(first);
      }

      msal.current = app;
      setSignedIn(!!app.getActiveAccount());
      setReady(true);
    })();

    return () => { cancelled = true; };
  }, [config]);

  /**
   * One place decides that the session is over.
   *
   * Remember where we were first — the user was mid-task, and coming back to
   * the client list when they were on a client is its own small failure.
   */
  useEffect(() =>
    onSignInNeeded((reason) => {
      setSignInReason(reason);
      setSignedIn((wasSignedIn) => {
        if (wasSignedIn) rememberWhereWeWere();
        return false;
      });
      if (config.mode === 'local') localStorage.removeItem(DEV_TOKEN);
    }),
  [config.mode]);

  const signIn = useCallback(async (devEmail?: string) => {
    if (config.mode === 'local') {
      if (!devEmail) throw new Error('Choose a user to sign in as.');
      const res = await fetch('/api/dev/token', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: devEmail }),
      });
      if (!res.ok) throw new Error(`Could not mint a token for ${devEmail}`);
      const { token } = (await res.json()) as { token: string };
      localStorage.setItem(DEV_TOKEN, token);
      setSignInReason(null);
      setSignedIn(true);
      return;
    }
    rememberWhereWeWere();
    await msal.current?.loginRedirect({ scopes: config.scopes });
  }, [config]);

  const signOut = useCallback(async () => {
    if (config.mode === 'local') {
      localStorage.removeItem(DEV_TOKEN);
      setSignedIn(false);
      return;
    }
    await msal.current?.logoutRedirect();
  }, [config]);

  const getToken = useCallback(async (): Promise<string> => {
    if (config.mode === 'local') {
      const token = localStorage.getItem(DEV_TOKEN);
      // Announced, not merely thrown — otherwise this surfaces on whichever
      // screen asked as an ordinary query error, beside a Staleness line
      // claiming the server is unreachable.
      if (!token) { announceSignInNeeded(); throw new NeedsSignIn(); }
      return token;
    }

    const app = msal.current;
    const account = app?.getActiveAccount();
    if (!app || !account) { announceSignInNeeded(); throw new NeedsSignIn(); }

    try {
      const { accessToken } = await app.acquireTokenSilent({
        scopes: config.scopes,
        account,
      });
      return accessToken;
    } catch {
      // Silent refresh has run out — the refresh token expired, or the
      // tenant now wants the user to sign in again. Neither is an error
      // worth showing; it is a redirect, and we come back to this page.
      rememberWhereWeWere();
      await app.acquireTokenRedirect({ scopes: config.scopes, account });
      announceSignInNeeded();
      throw new NeedsSignIn();
    }
  }, [config]);

  const value = useMemo<Session>(
    () => ({ config, isSignedIn, signInReason, getToken, signIn, signOut }),
    [config, isSignedIn, signInReason, getToken, signIn, signOut],
  );

  if (!ready) return <Splash>Preparing sign-in…</Splash>;
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export class NeedsSignIn extends Error {
  constructor() {
    super('Sign-in required');
    this.name = 'NeedsSignIn';
  }
}

export function Splash({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center p-6 text-neutral-500">
      {children}
    </div>
  );
}
